/**
 * The rail header, as a switcher between workspaces.
 *
 * The trigger names the workspace the route puts you in; the menu lists every
 * organization with the current one checked, then All organizations. It is
 * the only affordance for entering a tenant from the rail, which is why it
 * sits where finixt-ui's own rail puts its module switcher. The way back out,
 * the Platform row, stays above it rather than inside it.
 *
 * The list is fetched when the menu opens, not on every page load.
 */
import { listOrganizations } from '@optum/api-client/endpoints/organizations';
import type { OrganizationStatus } from '@optum/api-client/types';
import { useQuery } from '@tanstack/react-query';
import { forwardRef, useContext, useState, type ButtonHTMLAttributes } from 'react';

import { qk } from '../../lib/query-keys.js';
import { Dropdown, DropdownDivider, DropdownItem, DropdownSection } from '../../ui/Dropdown.js';
import { Avatar, Badge, Icon, ShellContext } from '../../ui/finixt.js';

const STATUS_TONE: Record<OrganizationStatus, 'ok' | 'warn' | 'info' | 'danger' | 'neutral'> = {
  active: 'ok',
  pending: 'warn',
  approved: 'info',
  rejected: 'danger',
  inactive: 'neutral',
};

function initials(name: string): string {
  return name.trim().slice(0, 2).toUpperCase() || '?';
}

export interface TenantSwitcherProps {
  /** Present on tenant routes; decides which workspace the trigger names. */
  orgId?: string | undefined;
  orgName?: string | undefined;
}

/** The trigger, drawn as finixt-ui's ModuleSwitcher trigger. */
interface TriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  subtitle: string;
  mark: string | null;
  collapsed: boolean;
}

const Trigger = forwardRef<HTMLButtonElement, TriggerProps>(function Trigger(
  { label, subtitle, mark, collapsed, ...rest },
  ref,
) {
  const [hover, setHover] = useState(false);
  return (
    <button
      ref={ref}
      type="button"
      title={collapsed ? label : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        width: collapsed ? undefined : '100%',
        boxSizing: 'border-box',
        padding: collapsed ? 0 : '5px 7px',
        border: `1px solid ${!collapsed && hover ? 'var(--border-hairline)' : 'transparent'}`,
        borderRadius: 10,
        background: !collapsed && hover ? 'var(--surface-card)' : 'transparent',
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'var(--font-sans)',
      }}
      {...rest}
    >
      <span
        style={{
          width: 26,
          height: 26,
          flexShrink: 0,
          borderRadius: 7,
          background: 'var(--surface-inverse)',
          color: 'var(--text-inverse)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 800,
          fontSize: 12,
        }}
      >
        {mark ?? <Icon name="space_dashboard" size={15} />}
      </span>
      {collapsed ? null : (
        <>
          <span style={{ minWidth: 0, flex: 1 }}>
            <span
              style={{
                display: 'block',
                fontSize: 9.5,
                letterSpacing: '.06em',
                textTransform: 'uppercase',
                color: 'var(--text-muted)',
              }}
              className="mono"
            >
              {subtitle}
            </span>
            <span
              style={{
                display: 'block',
                fontWeight: 700,
                fontSize: 14,
                letterSpacing: '-0.02em',
                color: 'var(--text-strong)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </span>
          </span>
          <Icon name="unfold_more" size={17} color="var(--text-soft)" style={{ flexShrink: 0 }} />
        </>
      )}
    </button>
  );
});

export function TenantSwitcher({ orgId, orgName }: TenantSwitcherProps) {
  const shell = useContext(ShellContext);
  const collapsed = Boolean(shell.railCollapsed);
  const [open, setOpen] = useState(false);
  const inOrg = Boolean(orgId);
  const label = inOrg ? (orgName ?? 'Organization') : 'Platform';

  const orgs = useQuery({
    queryKey: qk.orgs(),
    queryFn: () => listOrganizations(),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const tenants = orgs.data ?? [];

  return (
    <Dropdown
      width={282}
      open={open}
      onOpenChange={setOpen}
      trigger={
        <Trigger
          label={label}
          subtitle={inOrg ? 'Organization' : 'Workspace'}
          mark={inOrg ? initials(orgName ?? 'Org') : null}
          collapsed={collapsed}
        />
      }
    >
      <DropdownSection label="Organizations" />
      {orgs.isPending ? <Note>Loading organizations…</Note> : null}
      {orgs.isError ? <Note tone="danger">Could not load organizations.</Note> : null}
      {orgs.isSuccess && tenants.length === 0 ? <Note>No organizations.</Note> : null}
      {tenants.map((org) => {
        const current = org.id === orgId;
        return (
          <DropdownItem
            key={org.id}
            label={org.name}
            leading={<Avatar initials={initials(org.name)} size={20} />}
            trailing={
              org.status === 'active' ? undefined : (
                <Badge tone={STATUS_TONE[org.status]} mono>
                  {org.status}
                </Badge>
              )
            }
            {...(current ? { checked: true } : { to: `/orgs/${org.id}/overview` })}
          />
        );
      })}
      <DropdownDivider />
      <DropdownItem icon="apartment" label="All organizations" to="/organizations" />
    </Dropdown>
  );
}

/** A line of status inside the menu, where an item would otherwise be. */
function Note({ children, tone }: { children: string; tone?: 'danger' | undefined }) {
  return (
    <p
      style={{
        margin: 0,
        padding: '7px 9px',
        fontSize: 'var(--text-body-sm)',
        color: tone === 'danger' ? 'var(--danger)' : 'var(--text-faint)',
      }}
    >
      {children}
    </p>
  );
}
